import React, { createContext, useState, ReactNode, useEffect, useMemo } from 'react';
import { db } from '../firebase';
import { 
    collection, query, onSnapshot, orderBy, doc, getDoc, addDoc, updateDoc, deleteDoc, where, getDocs, writeBatch, Timestamp, setDoc, limit, startAt, endAt, getCountFromServer, increment
} from 'firebase/firestore';
import { useAuth } from '../hooks/useAuth';
import { useGuru } from './GuruContext';
import { Permit, PermitStatus, PermitContextType, NewPermitData, Santri, NewSantriData, PermitType, Role, Petugas, WhatsappLog, AbsensiLiburan, NewAbsensiLiburanData, AbsenRecord, NewAbsenRecordData, SyaratLiburan, NewSyaratLiburanData, KlinikRecord, NewKlinikRecordData, KlinikStatus, Kegiatan, NewKegiatanData, KelasRecord, NewKelasData, AsramaRecord, NewAsramaData, GuruRecord, NewGuruData, MurobbiRecord, NewMurobbiData, BlacklistEntry, NewBlacklistData, KegiatanAbsen, KegiatanAbsenStatus, TahunAjaranRecord, NewTahunAjaranData, KlinikConfig, Division, StructureMember } from '../types';
import { handleCheckoutNotification, handleCheckinNotification, resendMessageFromLog } from '../utils/whatsapp';
import { getOfficerName } from '../utils/officer';
import { useUserAccess } from './UserAccessContext';
import { useSantri } from './SantriContext';

export const PermitContext = createContext<PermitContextType | undefined>(undefined);

export const usePermit = () => {
    const context = React.useContext(PermitContext);
    if (context === undefined) {
        throw new Error('usePermit must be used within a PermitProvider');
    }
    return context;
};

const generateShortId = (prefix: 'CO' | 'CI'): string => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let result = '';
  for (let i = 0; i < 6; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return `${prefix}-${result.slice(0, 3)}-${result.slice(3, 6)}`;
};

export const PermitProvider: React.FC<{ children: ReactNode }> = ({ children }) => {

  const { authUser } = useAuth();
  const { isSuperAdmin, userPermissions } = useUserAccess();
  const guruContextData = useGuru();
  const { guruRecords } = guruContextData;

  const { santri, loading: santriLoading } = useSantri();

  const [permitLoading, setPermitLoading] = useState(true);
  const loading = permitLoading || guruContextData.loading || santriLoading;
  const [permits, setPermits] = useState<Permit[]>([]);
  const [blacklist, setBlacklist] = useState<BlacklistEntry[]>([]);
  const [whatsappLogs, setWhatsappLogs] = useState<WhatsappLog[]>([]);
  const [uniquePembimbing, setUniquePembimbing] = useState<string[]>([]);
  
  const [earlyCheckoutHours, setEarlyCheckoutHours] = useState<number>(1);
  const [scannerWithForm, setScannerWithForm] = useState<boolean>(true);


  // Shared helper: map Firestore doc → Permit
  const mapPermitDoc = (doc: any): Permit => {
      const data = doc.data();
      return {
          ...data,
          id: doc.id,
          creationTime: (data.creationTime as Timestamp)?.toDate(),
          status: data.status as PermitStatus,
          plannedDepartureTime: (data.plannedDepartureTime as Timestamp)?.toDate(),
          rencanaKembali: (data.rencanaKembali as Timestamp)?.toDate(),
          checkoutTime: (data.checkoutTime as Timestamp)?.toDate(),
          checkinTime: (data.checkinTime as Timestamp)?.toDate(),
      } as Permit;
  };

  // Compute needsPermitsAndLogs as a stable boolean — avoids race condition
  // where userPermissions/isSuperAdmin haven't loaded yet when the effect first fires.
  const needsPermitsAndLogs = useMemo(() => {
      if (!authUser) return false;
      const permData = userPermissions.find(p => p.email === authUser.email);
      const currentUserPerms = permData?.allowedMenus || [];
      return isSuperAdmin || currentUserPerms.some((menu: string) => [
          'pg-bagian-perizinan', 'pg-kepala-divisi', 'pg-departemen-pendidikan', 'pg-mudir',
          'pg-wali-kelas', 'pg-murobbi', 'pg-bagian-resepsionis', 'bagian-perizinan', 'resepsionis'
      ].includes(menu));
  }, [authUser, isSuperAdmin, userPermissions]);

  useEffect(() => {
    let isMounted = true;
    if (!authUser) {
      setPermits([]);
      setWhatsappLogs([]);
      setUniquePembimbing([]);
      setPermitLoading(false);
      return;
    }

    // If user doesn't need permits/logs, skip subscriptions entirely
    if (!needsPermitsAndLogs) {
      setPermits([]);
      setPermitLoading(false);
      return;
    }

    setPermitLoading(true);

    let unsubscribeActivePermits = () => {};
    let unsubscribeRecentPermits = () => {};
    let unsubscribeWhatsappLogs = () => {};
    let unsubscribeCheckoutSettings = () => {};
    let unsubscribeScannerSettings = () => {};
    let unsubscribeBlacklist = () => {};

    // --- OPTIMIZED PERMIT LISTENERS ---
    // 1. Get "Active" Permits (OUT / PENDING) - No time limit, ensuring Auto-Check works forever.
    const activePermitsQuery = query(
        collection(db, 'permits'),
        where('status', 'in', [PermitStatus.OUT, PermitStatus.PENDING_CHECKOUT])
    );

    // 2. Get "Recent History" Permits (Last 20 Days) - For Dashboard History.
    const twentyDaysAgo = new Date();
    twentyDaysAgo.setDate(twentyDaysAgo.getDate() - 20);
    const recentPermitsQuery = query(
        collection(db, 'permits'),
        where('creationTime', '>=', twentyDaysAgo),
        orderBy('creationTime', 'desc')
    );

    let activePermitsRaw: Permit[] = [];
    let recentPermitsRaw: Permit[] = [];

    const processAndSetPermits = () => {
        const now = new Date();
        const twentyFourHoursAgo = now.getTime() - (24 * 60 * 60 * 1000);

        // Merge and Deduplicate by ID
        const combinedMap = new Map<string, Permit>();
        [...activePermitsRaw, ...recentPermitsRaw].forEach(p => combinedMap.set(p.id, p));
        const mergedList = Array.from(combinedMap.values());

        const processedPermits = mergedList.map(p => {
             // System Check Logic
             let status = p.status;

             // Auto-Cancel Pending
             if (status === PermitStatus.PENDING_CHECKOUT && p.creationTime.getTime() < twentyFourHoursAgo) {
                 status = PermitStatus.CANCELED;
             }

             // Auto-Complete (Tidak Laporan) logic
             const twelveHoursAfterReturn = p.rencanaKembali ? new Date(p.rencanaKembali.getTime() + 12 * 60 * 60 * 1000) : null;
             if (
                 status === PermitStatus.OUT &&
                 (p.permitType === PermitType.KELUAR || p.permitType === PermitType.IZIN_TERBIMBING) &&
                 twelveHoursAfterReturn &&
                 now > twelveHoursAfterReturn
             ) {
                 status = PermitStatus.COMPLETED_BY_SYSTEM;
             }

             return { ...p, status };
        });

        // Sort desc by creationTime
        processedPermits.sort((a, b) => b.creationTime.getTime() - a.creationTime.getTime());

        setPermits(processedPermits);

        const pembimbingNames = new Set(
            processedPermits
                .filter(p => p.permitType === PermitType.IZIN_TERBIMBING && p.pembimbing)
                .map(p => p.pembimbing!)
        );
        setUniquePembimbing(Array.from(pembimbingNames).sort());
    };

    const setupPermitsSubscription = () => {
        unsubscribeActivePermits = onSnapshot(activePermitsQuery, snapshot => {
            activePermitsRaw = snapshot.docs.map(mapPermitDoc);
            processAndSetPermits();
        }, (error) => {
            console.error("PermitContext: activePermitsQuery error:", error);
        });

        unsubscribeRecentPermits = onSnapshot(recentPermitsQuery, snapshot => {
            recentPermitsRaw = snapshot.docs.map(mapPermitDoc);
            processAndSetPermits();
        }, (error) => {
            console.error("PermitContext: recentPermitsQuery error:", error);
        });
    };

    // --- END OPTIMIZED PERMITS ---

    const checkoutSettingsDocRef = doc(db, 'settings', 'checkout');
    unsubscribeCheckoutSettings = onSnapshot(checkoutSettingsDocRef, (docSnap) => {
        if (docSnap.exists()) {
            const data = docSnap.data();
            if (typeof data.earlyHours === 'number') {
                setEarlyCheckoutHours(data.earlyHours);
            }
        }
    });

    const scannerSettingsDocRef = doc(db, 'settings', 'scanner');
    unsubscribeScannerSettings = onSnapshot(scannerSettingsDocRef, (docSnap) => {
        if (docSnap.exists()) {
            const data = docSnap.data();
            if (typeof data.withForm === 'boolean') {
                setScannerWithForm(data.withForm);
            }
        }
    });

    const blacklistQuery = query(collection(db, 'blacklist'), orderBy('endDate', 'desc'));
    unsubscribeBlacklist = onSnapshot(blacklistQuery, snapshot => {
        const blacklistData = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
                id: doc.id,
                ...data,
                startDate: (data.startDate as Timestamp).toDate(),
                endDate: (data.endDate as Timestamp).toDate(),
            } as BlacklistEntry;
        });
        setBlacklist(blacklistData);
    });

    const twelveHoursAgo = new Date();
    twelveHoursAgo.setHours(twelveHoursAgo.getHours() - 12);
    const whatsappLogsQuery = query(
        collection(db, 'whatsapp_logs'),
        where('timestamp', '>=', twelveHoursAgo),
        orderBy('timestamp', 'desc')
    );
    const setupWhatsappLogsSubscription = () => {
        unsubscribeWhatsappLogs = onSnapshot(whatsappLogsQuery, snapshot => {
            const logsData = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    ...data,
                    timestamp: (data.timestamp as Timestamp).toDate(),
                } as WhatsappLog;
            });
            setWhatsappLogs(logsData);
        }, (error) => {
            console.error("PermitContext: whatsappLogsQuery error:", error);
        });
    };

    // Set up subscriptions immediately (needsPermitsAndLogs already confirmed true)
    try {
        setupPermitsSubscription();
        setupWhatsappLogsSubscription();
    } catch (e) {
        console.error("PermitContext: subscription setup error:", e);
    }

    setPermitLoading(false);

    return () => {
      isMounted = false;
      unsubscribeActivePermits();
      unsubscribeRecentPermits();
      unsubscribeCheckoutSettings();
      unsubscribeScannerSettings();
      unsubscribeBlacklist();
      unsubscribeWhatsappLogs();
    };
  }, [authUser, needsPermitsAndLogs]);


  const cekStatusIzinAktif = async (santriId: string): Promise<Permit | null> => {
      // Jika data global aktif sudah ada di memori (mode admin)
      if (permits.length > 0) {
          const active = permits.find(p => p.santriIds.includes(santriId) && (p.status === PermitStatus.OUT || p.status === PermitStatus.PENDING_CHECKOUT));
          if (active) return active;
      }

      // Jika belum ada (mode guru/wali santri), tarik on-demand hanya yang status aktif
      const q = query(
          collection(db, 'permits'),
          where('santriIds', 'array-contains', santriId),
          where('status', 'in', [PermitStatus.OUT, PermitStatus.PENDING_CHECKOUT])
      );
      const snap = await getDocs(q);
      if (snap.empty) return null;

      const docSnap = snap.docs[0];
      const data = docSnap.data();
      return {
          ...data,
          id: docSnap.id,
          creationTime: (data.creationTime as Timestamp)?.toDate(),
          status: data.status as PermitStatus,
          plannedDepartureTime: (data.plannedDepartureTime as Timestamp)?.toDate(),
          rencanaKembali: (data.rencanaKembali as Timestamp)?.toDate(),
          checkoutTime: (data.checkoutTime as Timestamp)?.toDate(),
          checkinTime: (data.checkinTime as Timestamp)?.toDate(),
      } as Permit;
  };

  const getPermitsBySantriId = async (santriId: string, month?: number, year?: number, yearString?: string) => {
      try {
          const q = query(
              collection(db, 'permits'),
              where('santriIds', 'array-contains', santriId),
              orderBy('creationTime', 'desc'),
              limit(200) // Cukup untuk riwayat lengkap satu santri
          );
          const snapshot = await getDocs(q);
          const rawPermits = snapshot.docs.map(doc => {
              const data = doc.data();
              return {
                  ...data,
                  id: doc.id,
                  creationTime: (data.creationTime as Timestamp)?.toDate(),
                  status: data.status as PermitStatus,
                  plannedDepartureTime: (data.plannedDepartureTime as Timestamp)?.toDate(),
                  rencanaKembali: (data.rencanaKembali as Timestamp)?.toDate(),
                  checkoutTime: (data.checkoutTime as Timestamp)?.toDate(),
                  checkinTime: (data.checkinTime as Timestamp)?.toDate(),
              } as Permit;
          });

          // Filter by status internally just in case
          let filtered = rawPermits.filter(p => p.status !== PermitStatus.CANCELED);

          // Apply date filters if provided
          if (yearString) {
             const parts = yearString.split('/');
             if (parts.length === 2) {
                 const startYear = parseInt(parts[0], 10);
                 const endYear = parseInt(parts[1], 10);
                 const startDate = new Date(startYear, 6, 1); // July 1st
                 const endDate = new Date(endYear, 5, 30); // June 30th

                 filtered = filtered.filter(p => {
                    const time = p.checkoutTime || p.plannedDepartureTime || p.creationTime;
                    return time && time >= startDate && time <= endDate;
                 });
             }
          } else if (month !== undefined && year !== undefined) {
              const startDate = new Date(year, month, 1);
              const endDate = new Date(year, month + 1, 0);
              filtered = filtered.filter(p => {
                    const time = p.checkoutTime || p.plannedDepartureTime || p.creationTime;
                    return time && time >= startDate && time <= endDate;
              });
          }

          // Compute system status
          const now = new Date();
          const twentyFourHoursAgo = now.getTime() - (24 * 60 * 60 * 1000);

          return filtered.map(p => {
             let status = p.status;
             if (status === PermitStatus.PENDING_CHECKOUT && p.creationTime && p.creationTime.getTime() < twentyFourHoursAgo) {
                 status = PermitStatus.CANCELED;
             }
             const twelveHoursAfterReturn = p.rencanaKembali ? new Date(p.rencanaKembali.getTime() + 12 * 60 * 60 * 1000) : null;
             if (
                 status === PermitStatus.OUT &&
                 (p.permitType === PermitType.KELUAR || p.permitType === PermitType.IZIN_TERBIMBING) &&
                 twelveHoursAfterReturn &&
                 now > twelveHoursAfterReturn
             ) {
                 status = PermitStatus.COMPLETED_BY_SYSTEM;
             }
             return { ...p, status };
          }).sort((a, b) => b.creationTime.getTime() - a.creationTime.getTime());

      } catch (error) {
          console.error("Error fetching individual santri permits:", error);
          return [];
      }
  };

  // deleteMultipleSantri handled by SantriContext
  
  // addMultipleSantri handled by SantriContext

  // updateMultipleSantri handled by SantriContext
  // getOfficerName → utils/officer.ts (P0.5)

  const addPermit = async (newPermitData: NewPermitData): Promise<any> => {
    const santriDataFull = santri.filter(s => newPermitData.santriIds.includes(s.id));
    const santriData = santriDataFull.map(s => ({
      id: s.id || "",
      nama: s.nama || "",
      nis: s.nis || "",
      kelas: s.kelas || "",
      asrama: s.asrama || "",
      murobbi: s.murobbi || "",
      waliKelas: s.waliKelas || "",
      noHp: s.noHp || "",
      rfid: s.rfid || "",
    })) as any;
    if (santriDataFull.length !== newPermitData.santriIds.length) return null;
    const { santriIds, customCheckinId, ...restOfData } = newPermitData;

    // For Clinic Permits, use the custom ID for BOTH checkoutId and checkinId (initially)
    // or just ensure the system can find it.
    // To satisfy the requirement "Same QR for everything", we set both to the custom ID if provided.
    const finalCheckoutId = customCheckinId || generateShortId('CO');
    const finalCheckinId = customCheckinId || null;

    const newPermitDoc = {
      ...restOfData,
      noWali: restOfData.noWali || null,
      pembimbing: restOfData.pembimbing || null,
      relatedKegiatanId: restOfData.relatedKegiatanId || null,
      checkoutId: finalCheckoutId,
      checkinId: finalCheckinId,
      santri: santriData,
      santriIds: newPermitData.santriIds,
      status: PermitStatus.PENDING_CHECKOUT,
      creationTime: new Date(),
      checkoutTime: null,
      checkinTime: null,
      petugasIzin: getOfficerName(guruRecords, authUser),
      petugasCheckout: null,
      petugasCheckin: null,
    };
    const docRef = await addDoc(collection(db, 'permits'), newPermitDoc);
    return { ...newPermitDoc, id: docRef.id };
  };

  const checkoutPermit = async (checkoutId: string): Promise<Permit | undefined> => {
    const q = query(collection(db, 'permits'), where('checkoutId', '==', checkoutId), where('status', '==', PermitStatus.PENDING_CHECKOUT));
    const snapshot = await getDocs(q);
    if (snapshot.empty) return undefined;

    const permitDoc = snapshot.docs[0];
    const permitData = permitDoc.data();
    const permitToCheckout = { 
        ...permitData, 
        id: permitDoc.id,
        creationTime: (permitData.creationTime as Timestamp).toDate(),
        plannedDepartureTime: (permitData.plannedDepartureTime as Timestamp).toDate(),
        rencanaKembali: (permitData.rencanaKembali as Timestamp).toDate(),
    } as Permit;

    const twentyFourHoursAgo = Date.now() - (24 * 60 * 60 * 1000);
    if (permitToCheckout.creationTime.getTime() < twentyFourHoursAgo) {
        await updateDoc(permitDoc.ref, { status: PermitStatus.CANCELED });
        throw new Error(`Izin ini sudah kedaluwarsa.`);
    }

    const isActivityPermit = !!permitToCheckout.relatedKegiatanId;
    if (!permitToCheckout.isClinicPermit && !isActivityPermit) {
      if (permitToCheckout.permitType === PermitType.PULANG || permitToCheckout.permitType === PermitType.IZIN_TERBIMBING) {
        const earliestCheckoutTime = new Date(permitToCheckout.plannedDepartureTime.getTime() - (earlyCheckoutHours * 60 * 60 * 1000));
        if (new Date() < earliestCheckoutTime) {
          throw new Error(`Checkout terlalu awal.`);
        }
      }
    }

    const officerName = getOfficerName(guruRecords, authUser);
    // If permit already has a checkinId (e.g. Clinic Permits with fixed QR), preserve it.
    // Otherwise, generate a new one.
    const checkinId = permitToCheckout.checkinId || generateShortId('CI');

    let checkoutTime = new Date();
    if (isActivityPermit && permitToCheckout.permitType === PermitType.PULANG && permitToCheckout.plannedDepartureTime) {
      checkoutTime = permitToCheckout.plannedDepartureTime;
    }

    const updateData: any = {
      status: PermitStatus.OUT,
      checkoutTime,
      checkinId,
      petugasCheckout: officerName,
    };

    // If this is a Clinic Permit being checked out by a Permission Officer, update the petugasIzin
    // to the current officer (Permission Officer) so the letter reflects the approver correctly.
    if (permitToCheckout.isClinicPermit) {
        updateData.petugasIzin = officerName;
    }

    await updateDoc(permitDoc.ref, updateData);
    const updatedPermitData = { ...permitToCheckout, ...updateData };
    const freshSantriData = updatedPermitData.santri.map(s => santri.find(dbSantri => dbSantri.id === s.id) || s);
    handleCheckoutNotification({ ...updatedPermitData, santri: freshSantriData });
    return updatedPermitData;
  };

  const checkoutPermitByDocId = async (docId: string): Promise<Permit | undefined> => {
    const permitRef = doc(db, 'permits', docId);
    const permitSnap = await getDoc(permitRef);

    if (!permitSnap.exists()) return undefined;

    const permitData = permitSnap.data();
    // Verify status is PENDING
    if (permitData.status !== PermitStatus.PENDING_CHECKOUT) {
        // If already OUT, maybe just return it? But usually implies an error in flow.
        // For robustness, if it's already OUT, we can return it as is.
        if (permitData.status === PermitStatus.OUT) {
             return {
                ...permitData,
                id: permitSnap.id,
                creationTime: (permitData.creationTime as Timestamp).toDate(),
                plannedDepartureTime: (permitData.plannedDepartureTime as Timestamp).toDate(),
                rencanaKembali: (permitData.rencanaKembali as Timestamp).toDate(),
                checkoutTime: (permitData.checkoutTime as Timestamp)?.toDate(),
            } as Permit;
        }
        return undefined;
    }

    const permitToCheckout = {
        ...permitData,
        id: permitSnap.id,
        creationTime: (permitData.creationTime as Timestamp).toDate(),
        plannedDepartureTime: (permitData.plannedDepartureTime as Timestamp).toDate(),
        rencanaKembali: (permitData.rencanaKembali as Timestamp).toDate(),
    } as Permit;

    // Check Expiration
    const twentyFourHoursAgo = Date.now() - (24 * 60 * 60 * 1000);
    if (permitToCheckout.creationTime.getTime() < twentyFourHoursAgo) {
        await updateDoc(permitRef, { status: PermitStatus.CANCELED });
        throw new Error(`Izin ini sudah kedaluwarsa.`);
    }

    // Check Time (Skip for Emergency/Clinic logic or Activity Appreciation Permits)
    const isActivityPermit = !!permitToCheckout.relatedKegiatanId;
    if (!permitToCheckout.isClinicPermit && !isActivityPermit) {
        if (permitToCheckout.permitType === PermitType.PULANG || permitToCheckout.permitType === PermitType.IZIN_TERBIMBING) {
            const earliestCheckoutTime = new Date(permitToCheckout.plannedDepartureTime.getTime() - (earlyCheckoutHours * 60 * 60 * 1000));
            if (new Date() < earliestCheckoutTime) {
                throw new Error(`Checkout terlalu awal.`);
            }
        }
    }

    const officerName = getOfficerName(guruRecords, authUser);
    const checkinId = permitToCheckout.checkinId || generateShortId('CI');

    let checkoutTime = new Date();
    if (isActivityPermit && permitToCheckout.permitType === PermitType.PULANG && permitToCheckout.plannedDepartureTime) {
        checkoutTime = permitToCheckout.plannedDepartureTime;
    }

    const updateData: any = {
      status: PermitStatus.OUT,
      checkoutTime,
      checkinId,
      petugasCheckout: officerName,
    };

    if (permitToCheckout.isClinicPermit) {
        updateData.petugasIzin = officerName;
    }

    await updateDoc(permitRef, updateData);
    const updatedPermitData = { ...permitToCheckout, ...updateData };
    const freshSantriData = updatedPermitData.santri.map(s => santri.find(dbSantri => dbSantri.id === s.id) || s);
    handleCheckoutNotification({ ...updatedPermitData, santri: freshSantriData });
    return updatedPermitData;
  };

  const checkinPermit = async (checkinId: string, reason?: string, acceptLateReason?: boolean): Promise<Permit | undefined> => {
    const q = query(collection(db, 'permits'), where('checkinId', '==', checkinId), where('status', '==', PermitStatus.OUT));
    const snapshot = await getDocs(q);
    if (snapshot.empty) return undefined;

    const permitDoc = snapshot.docs[0];
    const rawData = permitDoc.data();
    const permitData = {
        ...rawData,
        id: permitDoc.id,
        rencanaKembali: (rawData.rencanaKembali as Timestamp).toDate(),
        checkoutTime: (rawData.checkoutTime as Timestamp).toDate(),
        plannedDepartureTime: (rawData.plannedDepartureTime as Timestamp).toDate(),
        creationTime: (rawData.creationTime as Timestamp).toDate(),
    };
    const updatedData: any = { status: PermitStatus.COMPLETED, petugasCheckin: getOfficerName(guruRecords, authUser) };
    const isLate = new Date() > permitData.rencanaKembali;
    if (isLate && acceptLateReason) {
        updatedData.checkinTime = permitData.rencanaKembali;
        updatedData.keteranganTelat = 'Tepat Waktu (Diterima)';
        updatedData.alasanTelat = reason;
    } else {
        const checkinTime = new Date();
        updatedData.checkinTime = checkinTime;
        if (isLate) {
            const diffMs = checkinTime.getTime() - permitData.rencanaKembali.getTime();
            const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
            const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
            let durationString = [
                days > 0 ? `${days} hari` : '',
                hours > 0 ? `${hours} jam` : '',
                minutes > 0 ? `${minutes} menit` : ''
            ].filter(Boolean).join(' ');
            updatedData.keteranganTelat = 'Terlambat';
            updatedData.durasiTelat = durationString || 'beberapa saat';
            updatedData.alasanTelat = reason;
        } else {
            updatedData.keteranganTelat = 'Tepat Waktu';
        }
    }
    await updateDoc(permitDoc.ref, updatedData);

    // Clinic Integration: Update Clinic Record if linked
    if (permitData.relatedKlinikId) {
        try {
            const clinicRef = doc(db, 'klinik_records', permitData.relatedKlinikId);
            await updateDoc(clinicRef, { status: KlinikStatus.SEMBUH, tanggalSembuh: new Date(), jamSembuh: new Date().toLocaleTimeString('id-ID', {hour: '2-digit', minute:'2-digit'}) });
        } catch (e) {
            console.error("Failed to auto-update clinic record", e);
        }
    }

    const finalPermitData = { ...permitData, ...updatedData } as Permit;
    const freshSantriData = finalPermitData.santri.map(s => santri.find(dbSantri => dbSantri.id === s.id) || s);
    handleCheckinNotification({ ...finalPermitData, santri: freshSantriData });
    return finalPermitData;
  };

  const updatePermit = async (id: string, data: Partial<Permit>) => {
      await updateDoc(doc(db, 'permits', id), data);
  };

  const deletePermits = async (permitIds: string[]) => {
    if (permitIds.length === 0) return;
    const batch = writeBatch(db);
    permitIds.forEach(id => batch.delete(doc(db, 'permits', id)));
    await batch.commit();
  };
  
  const cancelPermit = async (permitId: string) => await updateDoc(doc(db, 'permits', permitId), { status: PermitStatus.CANCELED, keteranganTelat: 'Dibatalkan' });
  const extendPermitTime = async (permitId: string, newReturnDate: Date) => await updateDoc(doc(db, 'permits', permitId), { rencanaKembali: newReturnDate });
  const forceCheckin = async (permitId: string, actualCheckinTime: Date) => await updateDoc(doc(db, 'permits', permitId), { status: PermitStatus.COMPLETED, checkinTime: actualCheckinTime, keteranganTelat: 'Tidak Laporan', petugasCheckin: getOfficerName(guruRecords, authUser) });

  const addBlacklistEntry = async (data: NewBlacklistData): Promise<any> => await addDoc(collection(db, 'blacklist'), data);
  const updateBlacklistEntry = async (id: string, data: Partial<NewBlacklistData>) => await updateDoc(doc(db, 'blacklist', id), data);
  const deleteBlacklistEntry = async (id: string) => await deleteDoc(doc(db, 'blacklist', id));

  const updateEarlyCheckoutHours = async (hours: number) => {
    if (hours < 1 || hours > 15) throw new Error("Jam harus di antara 1 dan 15.");
    await setDoc(doc(db, 'settings', 'checkout'), { earlyHours: hours });
    setEarlyCheckoutHours(hours);
  };

  const updateScannerWithForm = async (value: boolean) => {
    await setDoc(doc(db, 'settings', 'scanner'), { withForm: value });
    setScannerWithForm(value);
  };

  // togglePortalGuru → SessionContext (P0.4)
  
  const resendWhatsappMessage = async (log: WhatsappLog) => await resendMessageFromLog(log);
  const deleteWhatsappLogs = async (logIds: string[]) => {
    if (logIds.length === 0) return;
    const batch = writeBatch(db);
    logIds.forEach(id => batch.delete(doc(db, 'whatsapp_logs', id)));
    await batch.commit();
  };

  const backfillPermitData = async (): Promise<number> => {
    const [permitsSnap, santriSnap] = await Promise.all([getDocs(collection(db, 'permits')), getDocs(collection(db, 'santri'))]);
    const santriMap = new Map<string, Santri>(santriSnap.docs.map(d => [d.id, { id: d.id, ...d.data() } as Santri]));
    const batch = writeBatch(db);
    let updatedCount = 0;
    permitsSnap.forEach(permitDoc => {
        const permit = permitDoc.data() as Permit;
        let needsUpdate = false;
        const updatedSantriArray = permit.santri.map(s => {
            const fullSantri = santriMap.get(s.id);
            if (fullSantri && (!s.nis || !s.rfid || !s.kelas || !s.asrama)) {
                needsUpdate = true;
                return fullSantri;
            }
            return s;
        });
        if (needsUpdate) {
            batch.update(permitDoc.ref, { santri: updatedSantriArray });
            updatedCount++;
        }
    });
    if (updatedCount > 0) await batch.commit();
    return updatedCount;
  };

  // addAbsensiLiburan handled by Context
  // updateAbsensiLiburan handled by Context
  // deleteAbsensiLiburan handled by Context
  // setActiveAbsensiLiburan handled by Context

  // fetchAbsenRecordsByEventId handled by Context

  // finalizeAbsensiLiburan handled by Context

  // addAbsenRecord handled by Context
  // updateAbsenRecord handled by Context
  // deleteAbsenRecord handled by Context
  // deleteMultipleAbsenRecords handled by Context

  // addSyaratLiburan handled by Context
  // updateSyaratLiburan handled by Context
  // deleteSyaratLiburan handled by Context

  // addKlinikRecord handled by Context
  // updateKlinikRecord handled by Context
  // deleteKlinikRecord handled by Context

  // addKegiatan handled by Context
  // updateKegiatan handled by Context
  // deleteKegiatan handled by Context

  // fetchKegiatanAbsen handled by Context

  // fetchKegiatanAbsenByDate handled by Context

  // addKegiatanAbsenBatch handled by Context

  const getSantriPermitHistory = async (santriId: string): Promise<Permit[]> => {
      const q = query(
        collection(db, 'permits'),
        where('santriIds', 'array-contains', santriId),
        orderBy('creationTime', 'desc'),
        limit(200)
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => {
          const data = doc.data();
          return {
              id: doc.id,
              ...data,
              creationTime: (data.creationTime as Timestamp)?.toDate(),
              plannedDepartureTime: (data.plannedDepartureTime as Timestamp)?.toDate(),
              rencanaKembali: (data.rencanaKembali as Timestamp)?.toDate(),
              checkoutTime: (data.checkoutTime as Timestamp)?.toDate(),
              checkinTime: (data.checkinTime as Timestamp)?.toDate(),
          } as Permit;
      });
  };

  // activateTahunAjaran handled by MasterDataContext

  // On-demand search across ALL permits (beyond 20-day window).
  // Only fires when user explicitly searches — one-time getDocs, not a subscription.
  // Uses existing creationTime index + client-side name filtering.
  const searchPermits = async (keyword: string, maxResults = 100): Promise<Permit[]> => {
      const q = query(
          collection(db, 'permits'),
          orderBy('creationTime', 'desc'),
          limit(maxResults)
      );
      const snapshot = await getDocs(q);
      const allPermits = snapshot.docs.map(mapPermitDoc);

      const lowerKeyword = keyword.toLowerCase();
      return allPermits.filter(p =>
          (p.pembimbing && p.pembimbing.toLowerCase().includes(lowerKeyword)) ||
          p.santri.some(s => s.nama.toLowerCase().includes(lowerKeyword))
      );
  };

  // addUserPermission handled by UserAccessContext
  // updateUserPermission handled by UserAccessContext
  // deleteUserPermission handled by UserAccessContext
  // checkUserAccess handled by UserAccessContext

  // updateKlinikConfig handled by Context

  return (
    <PermitContext.Provider value={{ 
        loading,
        permits, addPermit, checkoutPermit, checkoutPermitByDocId, checkinPermit, updatePermit, deletePermits, cancelPermit, extendPermitTime, forceCheckin, backfillPermitData,
        blacklist, addBlacklistEntry, updateBlacklistEntry, deleteBlacklistEntry,
        whatsappLogs, resendWhatsappMessage, deleteWhatsappLogs,
        earlyCheckoutHours, updateEarlyCheckoutHours,
        scannerWithForm, updateScannerWithForm,
        getSantriPermitHistory,
        getPermitsBySantriId, cekStatusIzinAktif,
        searchPermits,
        uniquePembimbing,
    }}>
      {!loading && children}
    </PermitContext.Provider>
  );
};
